import BaseService from "@/services/BaseService.ts";
import {toDeleteBlockedError} from "@/lib/errors/deleteBlockedError.ts";
import {supabase} from "@supabaseClient";
import {SaccoMembership} from "@/@types/sacco.models.ts";


/**
 * Service class for managing Sacco membership operations.
 * Extends the BaseService to provide specific functionality for managing Sacco membership records
 * in a persistent data store.
 */
export class SaccoMembershipService extends BaseService {

    /**
     * Creates a new Sacco membership record in the database.
     *
     * @param userId The unique identifier of the user for whom the Sacco membership is being created.
     * @param request The request object containing the Sacco membership details.
     * @return A promise that resolves to the newly created SaccoMembership object.
     */
    public async create(userId: string, request: SaccoMembershipCreateRequest): Promise<SaccoMembership> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_memberships')
                .insert({ ...payload, user_id: userId })
                .select()
                .single();
            if (error) throw error;
            return data as SaccoMembership;
        } catch (error) {
            console.error("Error creating Sacco membership", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates a Sacco membership record in the database with the given request data.
     *
     * @param {SaccoMembershipUpdateRequest} request - The request object containing the updated membership details.
     * @return {Promise<SaccoMembership>} A promise that resolves to the updated Sacco membership record.
     */
    async update(request: SaccoMembershipUpdateRequest): Promise<SaccoMembership> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_memberships')
                .update(payload)
                .eq('id', payload.id)
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error updating Sacco membership", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches all Sacco memberships associated with the specified user ID.
     *
     * @param {string} userId The unique identifier of the user whose memberships are to be fetched.
     * @return {Promise<SaccoMembership[]>} A promise that resolves to an array of SaccoMembership objects,
     *                                      ordered by creation date in descending order.
     * @throws Will log an error and handle it if there is an issue during the fetch process.
     */
    public async fetchAll(userId: string): Promise<SaccoMembership[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_memberships')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error fetching Sacco memberships", error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a record with the specified ID from the 'sacco_memberships' table.
     *
     * @param {string} id - The unique identifier of the record to be deleted.
     * @return {Promise<void>} A promise that resolves when the record is successfully deleted.
     *                          Rejects with an error if the deletion fails.
     */
    public async delete(id: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('sacco_memberships')
                .delete()
                .eq('id', id);
            if (error) {
                throw toDeleteBlockedError(error,
                    "This membership has deposits, shares, withdrawals, rebates or loans, so it can't be deleted.");
            }
        } catch (error) {
            console.error("Error deleting Sacco membership", error);
            await this.handleError(error);
        }
    }
}

export default new SaccoMembershipService();
